import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import OrderStatusBadge from './OrderStatusBadge';
import { toast } from 'react-toastify';
import './OrderDetailsModal.css';
import SerialNumberBadge from '../product/SerialNumberBadge';
import { exportDataToExcel } from '../../utilis/excelExporter';
import { exportOrderToCCIPLExcel } from '../../utilis/excelExporter';
import { useNavigate } from 'react-router-dom';
import { RiFileExcel2Line } from 'react-icons/ri'; // Import the icon

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const OrderDetailsModal = ({ order, onClose }) => {
    const [userDetails, setUserDetails] = useState(null);
    const [productDetailsMap, setProductDetailsMap] = useState({}); // To store fetched product details
    const [isLoading, setIsLoading] = useState(true);
    const isReturnOrder = order.orderStatus === 'returned' || order.orderStatus === 'partially_returned';
    const overlayRef = useRef(null);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchDataForModal = async () => {
            if (!order) {
                setIsLoading(false);
                return;
            }
            setIsLoading(true);
            try {
                const promises = [];

                // 1. Fetch User Details
                if (order.requestedBy) {
                    promises.push(axios.get(`${BACKEND_URL}/api/users/${order.requestedBy}`));
                } else {
                    promises.push(Promise.resolve(null)); // Placeholder if no requestedBy
                }

                // 2. Fetch Product Details for their notes
                if (order.products?.length > 0) {
                    const uniqueProductIds = [...new Set(order.products.map(p => p.product).filter(id => id))];
                    if (uniqueProductIds.length > 0) {
                        const productPromises = uniqueProductIds.map(id =>
                            axios.get(`${BACKEND_URL}/api/products/${id}`)
                        );
                        promises.push(
                            Promise.all(productPromises).then(responses =>
                                responses.reduce((acc, response) => {
                                    if (response.data) {
                                        acc[response.data._id] = response.data; // Store full product data
                                    }
                                    return acc;
                                }, {})
                            )
                        );
                    } else {
                        promises.push(Promise.resolve({})); // Placeholder for product details map
                    }
                } else {
                    promises.push(Promise.resolve({})); // Placeholder for product details map
                }
                
                const [userDetailsResponse, fetchedProductDetailsMap] = await Promise.all(promises);

                if (userDetailsResponse) setUserDetails(userDetailsResponse.data);
                setProductDetailsMap(fetchedProductDetailsMap || {});

            } catch (error) {
                console.error('Error fetching details for order modal:', error);
                toast.error('Failed to fetch some details for the order.');
            } finally {
                setIsLoading(false);
            }
        };
        fetchDataForModal();
    }, [order]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (overlayRef.current && overlayRef.current === event.target) {
                onClose();
            }
        };

        document.addEventListener('click', handleClickOutside);
        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, [onClose]);

    if (!order || isLoading) return <div className="loader"></div>;

    const handleExportSingleOrderToExcel = () => {
        if (!order) {
            toast.error("No order data to export.");
            return;
        }

        const dataForOrderSummarySheet = [
            ["Order Summary"], // Section Title
            ["Order Name:", order.orderName],
            ["Order Number:", order.orderNumber || 'N/A'],
            ["Destination:", order.destination],
            ["From Date:", new Date(order.fromDate).toLocaleDateString('he-IL')],
            ["Until Date:", new Date(order.untilDate).toLocaleDateString('he-IL')],
            ["Status:", order.orderStatus],
            [], // Empty row for spacing
            ["Requested By"],
            ["Name:", userDetails?.name || 'N/A'],
            ["Username:", userDetails?.username || 'N/A'],
            ["Email:", userDetails?.email || 'N/A'],
            [], // Empty row
            ["Products"],
            ["Product Name", "Product Code", "Quantity", "Serial Numbers", "Unit Price", "Total Price", "Product Total Weight"], // Product Headers
        ];

        order.products.forEach(product => {
            const unitPrice = product.price || 0;
            const totalPrice = product.quantity * unitPrice;
            const { weight, unit } = convertToKg(product.weight, product.weightUnit);
            // Calculate weight for one unit first, then multiply by quantity
            const singleProductWeightInKg = unit === 'g' ? weight / 1000 : weight;
            const totalProductWeightVal = product.quantity * singleProductWeightInKg;
            
            const totalWeightFinal = totalProductWeightVal < 1 && totalProductWeightVal > 0 
                                     ? { weight: totalProductWeightVal * 1000, unit: 'g' } 
                                     : { weight: totalProductWeightVal, unit: 'kg' };

            dataForOrderSummarySheet.push([
                product.productName,
                product.productCode,
                product.quantity,
                product.serialNumbers?.join(', ') || 'N/A',
                `$${unitPrice.toFixed(2)}`,
                `$${totalPrice.toFixed(2)}`,
                `${totalWeightFinal.weight.toFixed(totalWeightFinal.unit === 'g' ? 0 : 2)} ${totalWeightFinal.unit}`
            ]);
        });

        dataForOrderSummarySheet.push([]); // Empty row
        dataForOrderSummarySheet.push([ // Order Totals
            null, null, null, null, "Order Totals:",
            `$${order.products.reduce((sum, p) => sum + (p.quantity * (p.price || 0)), 0).toFixed(2)}`,
            `${totalWeight.weight.toFixed(totalWeight.unit === 'g' ? 0 : 2)} ${totalWeight.unit}` // This uses the already calculated totalWeight for the order
        ]);

        if (isReturnOrder && order.products.some(p => p.missingQuantity > 0 || p.returnedQuantity > 0)) {
            dataForOrderSummarySheet.push([]);
            dataForOrderSummarySheet.push(["Return Details"]);
            dataForOrderSummarySheet.push(["Product", "Product Code", "Ordered Qty", "Returned Qty", "Missing Qty", "Missing Serial Numbers"]);
            order.products.forEach(product => {
                if (product.returnedQuantity > 0 || product.missingQuantity > 0) { // Only include products with return activity
                    dataForOrderSummarySheet.push([
                        product.productName,
                        product.productCode,
                        product.quantity,
                        product.returnedQuantity || 0,
                        product.missingQuantity || 0,
                        product.missingSerialNumbers?.join(', ') || 'N/A'
                    ]);
                }
            });
        }

        // Prepare data for the "Products Only" sheet
        const dataForProductsSheet = [
            ["Product Name", "Product Code", "Quantity", "Serial Numbers", "Unit Price", "Total Price", "Product Total Weight"],
        ];

        order.products.forEach(product => {
            const unitPrice = product.price || 0;
            const totalPrice = product.quantity * unitPrice;
            const { weight, unit } = convertToKg(product.weight, product.weightUnit);
            // Calculate weight for one unit first, then multiply by quantity
            const singleProductWeightInKg = unit === 'g' ? weight / 1000 : weight;
            const totalProductWeightVal = product.quantity * singleProductWeightInKg;
            
            const totalWeightFinal = totalProductWeightVal < 1 && totalProductWeightVal > 0 
                                     ? { weight: totalProductWeightVal * 1000, unit: 'g' } 
                                     : { weight: totalProductWeightVal, unit: 'kg' };

            dataForProductsSheet.push([
                product.productName,
                product.productCode,
                product.quantity,
                product.serialNumbers?.join(', ') || 'N/A',
                `$${unitPrice.toFixed(2)}`,
                `$${totalPrice.toFixed(2)}`,
                `${totalWeightFinal.weight.toFixed(totalWeightFinal.unit === 'g' ? 0 : 2)} ${totalWeightFinal.unit}`
            ]);
        });

        dataForProductsSheet.push([]); // Empty row for spacing
        dataForProductsSheet.push([ // Order Totals for products sheet
            null, null, null, null, "Order Totals:",
            `$${order.products.reduce((sum, p) => sum + (p.quantity * (p.price || 0)), 0).toFixed(2)}`,
            `${totalWeight.weight.toFixed(totalWeight.unit === 'g' ? 0 : 2)} ${totalWeight.unit}`
        ]);

        const sheets = [
            { name: "Order Summary", data: dataForOrderSummarySheet },
            { name: "Products Only", data: dataForProductsSheet }
        ];

        exportDataToExcel(
            sheets,
            `Order_${order.orderNumber || 'Export'}`,
            'aoa',
            1 // Exporting 1 order
        );

    };

    const handleExportCCIP = async () => {
        try {
            if (!order) {
                toast.error("No order data to export.");
                return;
            }
            await exportOrderToCCIPLExcel(order, productDetailsMap);
        } catch (error) {
            console.error('Error exporting CCIPL:', error);
            toast.error('Failed to export CCIPL file.');
        }
    };
    

    const convertToKg = (weight, unit) => {
        if (unit === 'g') {
            const weightInKg = weight / 1000;
            return weightInKg < 1 ? { weight, unit: 'g' } : { weight: weightInKg, unit: 'kg' };
        }
        return { weight, unit };
    };

    const totalWeightInKg = order.products.reduce((sum, product) => {
        const { weight, unit } = convertToKg(product.weight, product.weightUnit);
        return sum + (product.quantity * (unit === 'g' ? weight / 1000 : weight));
    }, 0);

    const totalWeight = totalWeightInKg < 1 ? { weight: totalWeightInKg * 1000, unit: 'g' } : { weight: totalWeightInKg, unit: 'kg' };

    const handleNavigateToProductUpdate = async (productId) => {
        if (!productId) {
            toast.error("Product ID is missing. Cannot navigate to update page.");
            return;
        }
        // Consider setting a loading state specific to this action if it's a long operation
        // setIsLoading(true); 
        try {
            const response = await axios.get(`${BACKEND_URL}/api/products/${productId}`);
            const productToUpdate = response.data;

            if (productToUpdate) {
                onClose(); // Close the OrderDetailsModal
                navigate('/update-product', { state: { product: productToUpdate } });
            } else {
                toast.error("Could not fetch product details for update.");
            }
        } catch (error) {
            toast.error("Failed to navigate to product update page. The product might have been deleted or an error occurred.");
            console.error("Error preparing for product update navigation:", error);
        } finally {
            // setIsLoading(false);
        }
    };

    return (
        <div className="modal-overlay" ref={overlayRef}>
            <div className="modal-content">
                <button className="modal-close" onClick={onClose}>×</button>
                
                {/* Header Section with Order Info and Status */}
                <div className="modal-header">
                    <div className="order-basic-info">
                        <h2>Order Details</h2>
                        <p className="order-name">{order.orderName}</p>
                        <p className="order-number">#{order.orderNumber}</p>
                        <p className="order-dates">
                            {new Date(order.fromDate).toLocaleDateString('he-IL')} - {new Date(order.untilDate).toLocaleDateString('he-IL')}
                        </p>
                    </div>
                    <OrderStatusBadge status={order.orderStatus}/>
                    <div className="export-buttons-container">
                        <button className="export-single-order-btn" onClick={handleExportSingleOrderToExcel} title="Export this order to Excel">
                            <RiFileExcel2Line size={20} /> Export
                        </button>
                        <button className="export-single-order-btn" onClick={handleExportCCIP} title="Export CCIPL (CCIPL template)">
                            <RiFileExcel2Line size={20} /> Export CCIPL
                        </button>
                    </div>
                </div>

                <div className="order-details">
                    {/* User Information Section */}
                    <div className="user-info-section">
                        <h3>Requested By</h3>
                        <div className="user-info-grid">
                            <div className="user-info-item">
                                <span className="info-label">Name:</span>
                                <span className="info-value">{userDetails?.name || 'N/A'}</span>
                            </div>
                            <div className="user-info-item">
                                <span className="info-label">Username:</span>
                                <span className="info-value">{userDetails?.username || 'N/A'}</span>
                            </div>
                            <div className="user-info-item">
                                <span className="info-label">Email:</span>
                                <span className="info-value">{userDetails?.email || 'N/A'}</span>
                            </div>
                        </div>
                    </div>

                    {/* Order Information */}
                    <div className="destination-info">
                        <h3>Destination</h3>
                        <p>{order.destination}</p>
                    </div>

                    {/* Products Table */}
                    <div className="products-section">
                        <h3>Products</h3>
                        <table className="products-table">
                            <thead>
                                <tr>
                                    <th>Product Name</th>
                                    <th>Product Code</th>
                                    <th>Quantity</th>
                                    <th>Serial Number</th>
                                    {/* <th>Price</th> */}
                                    <th>Total Price</th>
                                    <th>Total Weight</th>
                                </tr>
                            </thead>
                            <tbody>
                                {order.products.map(product => {
                                    const totalPrice = product.quantity * product.price;
                                    const { weight, unit } = convertToKg(product.weight, product.weightUnit);
                                    const totalWeight = product.quantity * (unit === 'g' ? weight / 1000 : weight);
                                    const totalWeightFinal = totalWeight < 1 ? { weight: totalWeight * 1000, unit: 'g' } : { weight: totalWeight, unit: 'kg' };
                                    // Get the master data for the current product to access its serialNumberNotes
                                    const productMasterData = productDetailsMap[product.product];

                                    return (
                                        <tr key={product._id}>
                                            <td>{product.productName}</td>
                                            <td>{product.productCode}</td>
                                            <td>{product.quantity}</td>
                                            <td>
                                                {product.serialNumbers?.length > 0 ? (
                                                    <div className="serial-badges">
                                                        {product.serialNumbers.map(serial => (
                                                            <SerialNumberBadge 
                                                                // Assuming product.product contains the ID of the actual product document
                                                                onGoToUpdatePage={() => handleNavigateToProductUpdate(product.product)}
                                                                key={serial}
                                                                serialNumber={serial}
                                                                note={productMasterData?.serialNumberNotes?.find(snNote => snNote.value === serial)?.notes || ''} 
                                                            />
                                                        ))}
                                                    </div>
                                                ) : 'N/A'}
                                            </td>
                                            {/* <td>${product.price.toFixed(2)}</td> */}
                                            <td>${totalPrice.toFixed(2)}</td>
                                            <td>{totalWeightFinal.weight.toFixed(totalWeightFinal.unit === 'g' ? 0 : 2)} {totalWeightFinal.unit}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                            <tfoot>
                                <tr>
                                    <td colSpan="4" className="text-right"><strong>Order Totals:</strong></td>
                                    <td>
                                        <strong>
                                            ${order.products.reduce((sum, p) => sum + (p.quantity * p.price), 0).toFixed(2)}
                                        </strong>
                                    </td>
                                    <td>
                                        <strong>
                                            {totalWeight.weight.toFixed(totalWeight.unit === 'g' ? 0 : 2)} {totalWeight.unit}
                                        </strong>
                                    </td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>

                    {/* Return Details Table (only for returned orders) */}
                    {isReturnOrder && (
                        <div className="return-details-section">
                            <h3>Return Details</h3>
                            <table className="return-details-table">
                                <thead>
                                    <tr>
                                        <th>Product</th>
                                        <th>Product Code</th>
                                        <th>Asked Qty</th>
                                        <th>Returned Qty</th>
                                        <th>Missing Qty</th>
                                        <th>Missing Serial Numbers</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {order.products.map(product => {
                                        // Get the master data for the current product to access its serialNumberNotes
                                        const productMasterData = productDetailsMap[product.product];
                                        return (
                                            <tr key={product._id}>
                                                <td>{product.productName}</td>
                                                <td>{product.productCode}</td>
                                                <td>{product.quantity}</td>
                                                <td className="returned-qty">{product.returnedQuantity || 0}</td>
                                                <td className="missing-qty">{product.missingQuantity || 0}</td>
                                                <td className="missing-serials">
                                                    {product.missingSerialNumbers.length > 0 ? (
                                                        <div className="serial-badges">
                                                            {product.missingSerialNumbers.map(serial => (
                                                                <SerialNumberBadge 
                                                                    key={serial} 
                                                                    serialNumber={serial}
                                                                    note={productMasterData?.serialNumberNotes?.find(snNote => snNote.value === serial)?.notes || ''}
                                                                    isRemovable={false}
                                                                    onGoToUpdatePage={() => handleNavigateToProductUpdate(product.product)}
                                                                />
                                                            ))}
                                                        </div>
                                                    ) : 'N/A'}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default OrderDetailsModal;
